import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import type { ItemAttributeDef, ItemAttributeType } from '@/types/inventory';

/**
 * ADR-040 — authors a category's `attributeTemplate.itemAttributes`.
 * Each row is a typed attribute an item in this category can carry:
 *   TEXT        — free text
 *   NUMBER      — numeric, with an optional unit + min/max (e.g. GSM 10–1000)
 *   SELECT      — single pick from a fixed option list
 *   MULTISELECT — multi pick from a fixed option list
 * The value shape sent to the BE mirrors the runtime DTO
 * (AttributeTemplateInput): `multi` is derived from the type so the
 * existing options/multi validation keeps working.
 */

const TYPES: { value: ItemAttributeType; label: string }[] = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'SELECT', label: 'Single-select' },
  { value: 'MULTISELECT', label: 'Multi-select' },
];

function effectiveType(a: ItemAttributeDef): ItemAttributeType {
  if (a.type) return a.type;
  if (a.options && a.options.length > 0) return a.multi ? 'MULTISELECT' : 'SELECT';
  return 'TEXT';
}

interface Props {
  value: ItemAttributeDef[];
  onChange: (next: ItemAttributeDef[]) => void;
}

export function CategoryAttributeTemplateEditor({ value, onChange }: Props) {
  const rows = value ?? [];

  const patch = (idx: number, next: Partial<ItemAttributeDef>) => {
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...next } : r)));
  };

  const setType = (idx: number, type: ItemAttributeType) => {
    const row = rows[idx];
    const next: ItemAttributeDef = { ...row, type };
    next.multi = type === 'MULTISELECT';
    if (type === 'TEXT' || type === 'NUMBER') next.options = [];
    if (type !== 'NUMBER') {
      next.unit = undefined;
      next.min = undefined;
      next.max = undefined;
    }
    onChange(rows.map((r, i) => (i === idx ? next : r)));
  };

  const add = () =>
    onChange([...rows, { name: '', type: 'TEXT', required: false, multi: false }]);
  const remove = (idx: number) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2 border-t pt-4">
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-medium text-foreground">Item attributes</div>
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Add attribute
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Fields every item in this category can capture — e.g. a Fabric category with
        GSM (number), Width (number, inch), Composition, Finish (single-select).
      </p>

      {rows.length === 0 && (
        <div className="rounded-md border border-dashed bg-muted/30 px-3 py-3 text-[12px] text-muted-foreground">
          No attributes yet. Add GSM, Width, Composition, etc. so items in this
          category capture structured data.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((row, idx) => {
          const type = effectiveType(row);
          return (
            <div
              key={idx}
              className="space-y-2 rounded-md border bg-card px-3 py-2.5"
            >
              <div className="flex items-start gap-2">
                <Input
                  aria-label="Attribute name"
                  value={row.name}
                  onChange={(e) => patch(idx, { name: e.target.value })}
                  placeholder="Attribute name (e.g. GSM)"
                  maxLength={60}
                  className="flex-1"
                />
                <Select value={type} onValueChange={(v) => setType(idx, v as ItemAttributeType)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(idx)}
                  aria-label="Remove attribute"
                >
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>

              {(type === 'SELECT' || type === 'MULTISELECT') && (
                <Input
                  aria-label="Options"
                  value={(row.options ?? []).join(', ')}
                  onChange={(e) =>
                    patch(idx, {
                      options: e.target.value
                        .split(',')
                        .map((o) => o.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="Comma-separated options (e.g. Woven, Knit, Denim)"
                />
              )}

              {type === 'NUMBER' && (
                <div className="flex items-center gap-2">
                  <Input
                    aria-label="Unit"
                    value={row.unit ?? ''}
                    onChange={(e) => patch(idx, { unit: e.target.value || undefined })}
                    placeholder="Unit (GSM, inch, %)"
                    className="w-[130px]"
                  />
                  <Input
                    aria-label="Min"
                    type="number"
                    value={row.min ?? ''}
                    onChange={(e) =>
                      patch(idx, {
                        min: e.target.value === '' ? undefined : Number(e.target.value),
                      })
                    }
                    placeholder="Min"
                    className="w-[90px]"
                  />
                  <Input
                    aria-label="Max"
                    type="number"
                    value={row.max ?? ''}
                    onChange={(e) =>
                      patch(idx, {
                        max: e.target.value === '' ? undefined : Number(e.target.value),
                      })
                    }
                    placeholder="Max"
                    className="w-[90px]"
                  />
                </div>
              )}

              <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={!!row.required}
                  onChange={(e) => patch(idx, { required: e.target.checked })}
                  className="h-3.5 w-3.5 accent-primary"
                />
                Required
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
